import React, { useCallback, useEffect, useState } from 'react'
import { Avatar, Divider, Menu as MuiMenu, MenuItem as MuiMenuItem, Stack } from '@mui/material'
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar'
import nav from 'src/router/nav'
import styles from './SideBar.module.scss'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { MdOutlineCloseFullscreen } from 'react-icons/md'
import cmsDefaultAva from 'src/images/defaultAva.png'
import { getEmployeeProfile } from 'src/apis/employeeAPI'
import { useModal } from 'src/context/ModalContext'
import { logout } from 'src/apis/authAPI'
import { GoGear } from 'react-icons/go'
import { CgProfile } from 'react-icons/cg'
import { LuLogOut } from 'react-icons/lu'
import ModalChangePassword from '../Modal/ModalChangePassword'

function SideBar({ show, setShow }) {
	const location = useLocation()
	const navigate = useNavigate()
	const pathname = location.pathname?.split('/').splice(0, 2).join('/')
	const { openModal } = useModal()

	const [filteredNav, setFilteredNav] = useState<typeof nav>([])
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const [openPasswordModal, setOpenPasswordModal] = useState(false)
	const accountMenuOpen = Boolean(anchorEl)
	const info = JSON.parse(localStorage.getItem('info') || '{}')

	const handleOpenAccountMenu = useCallback((event) => {
		setAnchorEl(event.currentTarget)
	}, [])

	const handleCloseAccountMenu = useCallback(() => {
		setAnchorEl(null)
	}, [])

	const handleOpenPasswordModal = useCallback(() => {
		handleCloseAccountMenu()
		setOpenPasswordModal(true)
	}, [handleCloseAccountMenu])

	const handleClosePasswordModal = useCallback(() => {
		setOpenPasswordModal(false)
	}, [])

	const handleGoToProfile = useCallback(() => {
		handleCloseAccountMenu()
		navigate('/my-profile')
	}, [handleCloseAccountMenu, navigate])

	const handleLogout = useCallback(async () => {
		try {
			await logout({
				refresh_token: JSON.parse(
					localStorage.getItem('refresh_token') || 'null',
				),
				access_token: JSON.parse(localStorage.getItem('token') || 'null'),
			})
		} catch (error) {
		} finally {
			localStorage.clear()
			window.location.href = '/login'
		}
	}, [])

	const handleConfirmLogout = useCallback(() => {
		openModal({
			type: 'confirm',
			label: <div>Do you want to logout?</div>,
			handleConfirm: handleLogout,
		})
	}, [handleLogout, openModal])

	useEffect(() => {
		const fetchProfile = async () => {
			try {
				// setFilteredNav(nav)

				const res = await getEmployeeProfile()
				// 500 Internal Server Error return full role
				if (res?.data?.code === 500) {
					setFilteredNav(nav)
					return
				}


				const info = res?.data?.results?.object ?? res?.data ?? {}
				const userRole: string = info?.role
				const employeeRoles: string[] = Array.isArray(info?.employee_roles)
					? info.employee_roles.map((role: any) => role.name)
					: []
				const { data } = res

					const rawEmployeeRoles =
						data?.results?.object?.employee_roles ?? data?.results?.employee_roles ?? []

				// update Location stograge
				const employee_roles = Array.isArray(rawEmployeeRoles)
						? rawEmployeeRoles.map((role: any) =>
							typeof role === 'string' ? role : role?.name || '',
						).filter(Boolean)
						: rawEmployeeRoles
							? [typeof rawEmployeeRoles === 'string' ? rawEmployeeRoles : rawEmployeeRoles?.name || ''].filter(Boolean)
							: []

					localStorage.setItem(
						'info',
						JSON.stringify({
							...data?.results?.object,
							employee_roles,
						}),
					)

				// console.log(employeeRoles);

				if (userRole === 'SUPERADMIN') {
					setFilteredNav(nav)
					return
				}

				const isAccessible = (permission?: string, role?: string) => {
					if (role && userRole !== role) return false
					if (!permission) return true
					return employeeRoles.some(
						(role) => role === permission || permission.startsWith(role + ':'),
					)
				}

				const filtered = nav
					.map((item) => {
						if (item.action === 'logout') {
							return null
						}

						if (item.children?.length) {
							const visibleChildren = item.children.filter((child) =>
								isAccessible(child.permission, child.role),
							)
							return visibleChildren.length > 0
								? { ...item, children: visibleChildren }
								: null
						}
						return isAccessible(item.permission, item.role) ? item : null
					})
					.filter(Boolean)

				setFilteredNav(filtered as typeof nav)
			} catch (error) {
				console.error('Failed to fetch employee profile', error)

				setFilteredNav(nav)
				return
			}
		}

		fetchProfile()
	}, [])

	return (
		<Stack className={`${styles.wrapper} ${show ? styles.show : styles.hide}`}>
			<Stack
				className={`${styles.container} ${show ? styles.show : styles.hide}`}
			>
				<button
					type="button"
					className={styles.accountButton}
					onClick={handleOpenAccountMenu}
				>
					<Avatar
						src={cmsDefaultAva}
						alt={info?.name || 'Account'}
						sx={{ width: 34, height: 34, flexShrink: 0 }}
					/>
					<span className={styles.accountInfo}>
						<span className={styles.accountName}>{info?.name || 'Account'}</span>
						<span className={styles.accountRole}>{info?.role || '-'}</span>
					</span>
				</button>
				<MuiMenu
					anchorEl={anchorEl}
					open={accountMenuOpen}
					onClose={handleCloseAccountMenu}
					className={styles.accountMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
				>
					<Stack className={styles.accountMenuSummary}>
						<Avatar
							src={cmsDefaultAva}
							alt={info?.name || 'Account'}
							sx={{ width: 34, height: 34 }}
						/>
						<div className={styles.accountText}>
							<div className={styles.accountMenuName}>{info?.name || 'Account'}</div>
							<div className={styles.accountMenuRole}>{info?.role || '-'}</div>
						</div>
					</Stack>
					<Divider />
					<MuiMenuItem onClick={handleOpenPasswordModal}>
						<Stack direction="row" spacing={1} alignItems="center">
							<GoGear />
							<span>Change password</span>
						</Stack>
					</MuiMenuItem>
					<MuiMenuItem onClick={handleGoToProfile}>
						<Stack direction="row" spacing={1} alignItems="center">
							<CgProfile />
							<span>My profile</span>
						</Stack>
					</MuiMenuItem>
					<MuiMenuItem onClick={() => {
						handleCloseAccountMenu()
						handleConfirmLogout()
					}}>
						<Stack direction="row" spacing={1} alignItems="center">
							<LuLogOut />
							<span>Logout</span>
						</Stack>
					</MuiMenuItem>
				</MuiMenu>
				<Sidebar className={styles.sidebar}>
					<Menu className={styles.menu}>
						{filteredNav.map((item) =>
							item?.children?.length > 0 ? (
								<SubMenu
									className={styles.SubMenu}
									defaultOpen={
										item?.children?.map((child) => child.to).includes(pathname)
											? true
											: false
									}
									label={item?.name}
									key={item?.name}
									icon={item?.icon}
								>
									{item?.children?.map((child) => {
										return (
											<MenuItem
												key={child?.name}
												icon={child?.icon}
												className={`${styles.Dropdown} ${pathname === child.to ? styles.active : ''
													} `}
												component={<Link to={child.to} />}
											>
												{child?.name}
											</MenuItem>
										)
									})}
								</SubMenu>
							) : (
								<div key={item?.name}>
									<MenuItem
										icon={item?.icon}
										className={`${styles.MenuItem} ${pathname === item?.to ? styles.active : ''
											} `}
										onClick={() => {
											if (item?.action === 'logout') {
												handleConfirmLogout()
											}
										}}
										{...(item?.to ? { component: <Link to={item.to} /> } : {})}
									>
										{item?.name}
									</MenuItem>
								</div>
							),
						)}
						<br /> <br /> <br />
					</Menu>
				</Sidebar>
				<MdOutlineCloseFullscreen
					onClick={() => setShow((pre) => !pre)}
					className={styles.closeSidebar}
				></MdOutlineCloseFullscreen>
			</Stack>
			{openPasswordModal && (
				<ModalChangePassword
					open={openPasswordModal}
					handleClose={handleClosePasswordModal}
				/>
			)}
		</Stack>
	)
}

export default React.memo(SideBar)
